import { gql, useMutation, useQuery } from '@apollo/client';
import { Box, Divider, Link, Stack } from '@chakra-ui/react';
import * as React from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd';

import { NavigationButton } from '@/components/NavigationButton';
import { NavigationButtonMenuable } from '@/components/NavigationButtonMenuable';
import { useMemberManagement } from '@/hooks/useMemberManagement';

import { DragIcon } from '@/imports/ui/chakra/customIcon/dragIcon';
import { NavProfileIcon } from '@/imports/ui/chakra/customIcon/NavProfileIcon';
import { NavMembershipIcon } from '@/imports/ui/chakra/customIcon/NavMembeshipIcon';
import { NavShopingIcon } from '@/imports/ui/chakra/customIcon/NavShopingIcon';
import { NavNotificationIcon } from '@/imports/ui/chakra/customIcon/NavNotificationIcon';
import { NavBadgesIcon } from '@/imports/ui/chakra/customIcon/NavBadgesIcon';
import { NavPaymentIcon } from '@/imports/ui/chakra/customIcon/NavPaymentIcon';
import { NavPrivacyIcon } from '@/imports/ui/chakra/customIcon/NavPrivacyIcon';
import { NavTermsIcon } from '@/imports/ui/chakra/customIcon/NavTermsIcon';
import { NavPrivacyDocIcon } from '@/imports/ui/chakra/customIcon/NavPrivacyDocIcon';

import { HummusNavigation_Query } from './graphql-types/HummusNavigation_Query';
import { ChevronDownIcon, ChevronUpIcon, ClipboardIcon } from '@/imports/ui/chakra/feather';

const QUERY = gql`
  query HummusNavigation_Query {
    tenant {
      result {
        _id
        privacyPolicyLink {
          url
          text
        }
        termsAndConditionsLink {
          url
          text
        }
        customNavLinkV2 {
          url
          text
        }
      }
    }
  }
`;

const GET_PROFILE_NAVIGATION_ORDER = gql`
  query GetProfileNavigationOrder {
    getProfileNavigationOrder(input: { pageType: PROFILE_NAVIGATION }) {
      userId
      pageType
      navigationOrder
    }
  }
`;

const UPSERT_PROFILE_NAVIGATION_ORDER = gql`
  mutation UpsertProfileNavigationOrder(
    $input: UpsertProfileNavigationOrderInput!
  ) {
    upsertProfileNavigationOrder(input: $input) {
      userId
      pageType
      navigationOrder
    }
  }
`;

type NavigationId =
  | 'profile'
  | 'membership'
  | 'membershipRequests'
  | 'payment'
  | 'orders'
  | 'notifications'
  | 'privacy'
  | 'badges';

type NavigationItem = {
  id: NavigationId;
};

const DEFAULT_NAVIGATION_ORDER: NavigationId[] = [
  'profile',
  'membership',
  'membershipRequests',
  'payment',
  'orders',
  'notifications',
  'privacy',
  'badges',
];

// Inserts any id present in DEFAULT_NAVIGATION_ORDER but missing from a
// user's saved order (e.g. a nav item added after they last saved a custom
// order) right after its default predecessor, while leaving the rest of
// their saved ordering untouched.
const backfillMissingNavigationIds = (
  savedOrder: NavigationId[]
): NavigationId[] => {
  const result = [...savedOrder];
  DEFAULT_NAVIGATION_ORDER.forEach((id, defaultIndex) => {
    if (result.includes(id)) {
      return;
    }
    const previousDefaultId = DEFAULT_NAVIGATION_ORDER[defaultIndex - 1];
    const insertAfterIndex = previousDefaultId
      ? result.indexOf(previousDefaultId)
      : -1;
    result.splice(insertAfterIndex + 1, 0, id);
  });
  return result;
};

const reorder = <T,>(list: T[], startIndex: number, endIndex: number): T[] => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);

  result.splice(endIndex, 0, removed);

  return result;
};

export const HummusNavigation: React.FC = () => {
  const query = useQuery<HummusNavigation_Query>(QUERY);

  const navigationOrderQuery = useQuery(GET_PROFILE_NAVIGATION_ORDER, {
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
  });

  const [upsertProfileNavigationOrder] = useMutation(
    UPSERT_PROFILE_NAVIGATION_ORDER,
    {
      update(cache, { data }) {
        const updated = data?.upsertProfileNavigationOrder;

        if (!updated) {
          return;
        }

        cache.writeQuery({
          query: GET_PROFILE_NAVIGATION_ORDER,
          data: {
            getProfileNavigationOrder: updated,
          },
        });
      },
    },
  );

  const { isActive } = useMemberManagement();

  const privacyPolicyLink = query.data?.tenant.result?.privacyPolicyLink;
  const termsAndConditionsLink =
    query.data?.tenant.result?.termsAndConditionsLink;
  const customNavLinkV2 = query.data?.tenant.result?.customNavLinkV2;

  const [navigationItems, setNavigationItems] = React.useState<
    NavigationItem[]
  >(DEFAULT_NAVIGATION_ORDER.map((id) => ({ id })));

  React.useEffect(() => {
    if (navigationOrderQuery.loading) {
      return ;
    }

    const apiOrder =
      navigationOrderQuery.data?.getProfileNavigationOrder?.navigationOrder;

    const savedOrder: NavigationId[] =
      apiOrder && apiOrder.length > 0 ? apiOrder : DEFAULT_NAVIGATION_ORDER;

    // Backfill any ids added to DEFAULT_NAVIGATION_ORDER after this user's
    // order was first saved (e.g. a brand-new nav item) - otherwise a user
    // with a pre-existing saved order would never see it, since `savedOrder`
    // itself is missing the new id entirely.
    const order = backfillMissingNavigationIds(savedOrder);

    const visibleOrder = order.filter((id: NavigationId) => {
      if (
        (id === 'membership' || id === 'membershipRequests' || id === 'payment') &&
        !isActive
      ) {
        return false;
      }

      return DEFAULT_NAVIGATION_ORDER.includes(id);
    });

    setNavigationItems(visibleOrder.map((id: NavigationId) => ({ id })));
  }, [navigationOrderQuery.loading, navigationOrderQuery.data, isActive]);

  const saveNavigationOrder = React.useCallback(
    async (items: NavigationItem[]) => {
      try {
        await upsertProfileNavigationOrder({
          variables: {
            input: {
              pageType: 'PROFILE_NAVIGATION',
              navigationOrder: items.map((item) => item.id),
            },
          },
          optimisticResponse: {
            upsertProfileNavigationOrder: {
              __typename: 'UpsertProfileNavigationOrderOutput',
              userId:
                navigationOrderQuery.data?.getProfileNavigationOrder?.userId ??
                '',
              pageType: 'PROFILE_NAVIGATION',
              navigationOrder: items.map((item) => item.id),
            },
          },
        });
      } catch (error) {
        console.error('Failed to save profile navigation order', error);
      }
    },
    [navigationOrderQuery.data, upsertProfileNavigationOrder],
  );

  const onDragEnd = async (result: DropResult) => {
    const { source, destination } = result;

    if (!destination || source.index === destination.index) {
      return;
    }

    const nextItems = reorder(
      navigationItems,
      source.index,
      destination.index,
    );

    setNavigationItems(nextItems);

    await saveNavigationOrder(nextItems);
  };

  const isPrivacyOpen = location.pathname.startsWith("/me/privacy");

  const renderNavigationItem = (id: NavigationId) => {
    switch (id) {
      case 'profile':
        return (
          <NavigationButton
            href="/me/profile"
            leftIcon={
              <>
                <DragIcon color="primary.500" boxSize="20px" mt="-2px" mr="4px" cursor="grab" />
                <NavProfileIcon color="primary.500" boxSize="22px" />
              </>
            }
            isExact
            newView={true}
          >
            Profile
          </NavigationButton>
        );

      case 'membership':
        return (
          <NavigationButton
            href="/me/membership"
            leftIcon={
              <>
                <DragIcon color="primary.500" boxSize="20px" mt="-1px" mr="4px" cursor="grab" />
                <NavMembershipIcon color="primary.500" boxSize="22px" />
              </>
            }
            newView={true}
          >
            Membership
          </NavigationButton>
        );

      case 'membershipRequests':
        return (
          <NavigationButton
            href="/me/membership-requests"
            leftIcon={
              <>
                <DragIcon color="primary.500" boxSize="20px" mt="-1px" mr="4px" cursor="grab" />
                <ClipboardIcon color="primary.500" boxSize="22px" />
              </>
            }
            newView={true}
          >
            Membership Requests
          </NavigationButton>
        );

      case 'payment':
        return (
          <NavigationButton
            href="/me/payment"
            leftIcon={
              <>
                <DragIcon color="primary.500" boxSize="20px" mt="-1px" mr="4px" cursor="grab" />
                <NavPaymentIcon color="primary.500" boxSize="22px" />
              </>
            }
            newView={true}
          >
            Payment
          </NavigationButton>
        );

      case 'orders':
        return (
          <NavigationButton
            href="/me/orders"
            leftIcon={
              <>
                <DragIcon color="primary.500" boxSize="20px" mt="-1px" mr="4px" cursor="grab" />
                <NavShopingIcon color="primary.500" boxSize="22px" />
              </>
            }
            newView={true}
          >
            Orders
          </NavigationButton>
        );

      case 'notifications':
        return (
          <NavigationButton
            href="/me/notifications"
            leftIcon={
              <>
                <DragIcon color="primary.500" boxSize="20px" mt="-1px" mr="4px" cursor="grab" />
                <NavNotificationIcon color="primary.500" boxSize="22px" />
              </>
            }
            newView={true}
          >
            Notifications
          </NavigationButton>
        );

      case 'privacy':
      return (
        <NavigationButtonMenuable
          leftIcon={
            <>
              <DragIcon color="primary.500" boxSize="20px" mt="-1px" mr="4px" cursor="grab" />
              <NavPrivacyIcon color="primary.500" boxSize="22px" />
            </>
          }
          rightIcon={
            isPrivacyOpen ? (
              <ChevronUpIcon boxSize={5} color="primary.500" position="absolute" right="10px" top="10px"/>
            ) : (
              <ChevronDownIcon boxSize={5} color="primary.500" position="absolute" right="10px" top="10px"/>
            )
          }
          text="Privacy"
          href="/me/privacy"
          matchHref="/me/privacy"
          style={{ borderRadius: '0 6px 6px 0' }}
          _hover={{ bg: 'secondary.500' }}
          w="full"
          newView={true}
        >
        <Box
        ml="30px"
        mt="10px"
        pl="16px"
        position="relative"
        _before={{
          content: '""',
          position: 'absolute',
          left: "-5px",
          top: "0",
          bottom: "0",
          width: "2px",
          bg: "primary.500",
          borderRadius: "full",
        }}
      >
        <NavigationButtonMenuable
          text="Privacy Settings"
          href="/me/privacy"
          matchHref="/me/privacy"
          colorScheme="secondary"
          style={{ borderRadius: "6px" }}
          mb="3"
          _hover={{ bg: "secondary.500" }}
          w="full"
          isExact
        />
        <NavigationButtonMenuable
          text="Opt Ins"
          href="/me/privacy/consent-management"
          matchHref="/me/privacy/consent-management"
          colorScheme="secondary"
          style={{ borderRadius: "6px" }}
          _hover={{ bg: "secondary.500" }}
          w="full"
          isExact
        />
      </Box>
        </NavigationButtonMenuable>
      );
      
      case 'badges':
        return (
          <NavigationButton
            href="/me/badges"
            leftIcon={
              <>
                <DragIcon color="primary.500" boxSize="20px" mt="-1px" mr="4px" cursor="grab" />
                <NavBadgesIcon color="primary.500" boxSize="22px" />
              </>
            }
            newView={true}
          >
            Badges
          </NavigationButton>
        );

      default:
        return null;
    }
  };

  if (navigationOrderQuery.loading) {
    return null;
  }
  return (
    <Stack spacing="2" h="full">
      <Stack mb="auto">
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="profile-navigation">
            {(provided) => (
              <Stack ref={provided.innerRef} {...provided.droppableProps}>
                {navigationItems.map((item, index) => (
                  <Draggable
                    key={item.id}
                    draggableId={item.id}
                    index={index}
                  >
                    {(provided) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                      >
                        {renderNavigationItem(item.id)}
                      </Box>
                    )}
                  </Draggable>
                ))}

                {provided.placeholder}
              </Stack>
            )}
          </Droppable>
        </DragDropContext>

        <Divider />
      </Stack>

      <Stack spacing="4">
        <Link
          href={termsAndConditionsLink?.url}
          isExternal
          color="textSecondary.500"
          px="5"
          display="flex"
          justifyContent="flex-start"
        >
          <NavTermsIcon color="primary.500" boxSize="20px" mt="-1px" mr="6px" />
          {termsAndConditionsLink?.text ?? 'Terms and Conditions'}
        </Link>

        <Link
          href={privacyPolicyLink?.url}
          isExternal
          color="textSecondary.500"
          px="5"
        >
          <NavPrivacyDocIcon
            color="primary.500"
            boxSize="20px"
            mt="-1px"
            mr="6px"
          />
          {privacyPolicyLink?.text ?? 'Privacy Policy'}
        </Link>

        {customNavLinkV2 && (
          <Link
            href={customNavLinkV2.url}
            isExternal
            color="textSecondary.500"
            px="5"
          >
            {customNavLinkV2.text}
          </Link>
        )}
      </Stack>
    </Stack>
  );
};